<template>
    <div
        class="multi-select-wrapper"
        ref="root"
        :class="$attrs.class"
        :style="$attrs.style"
    >
        <q-select
            v-model="internalValue"
            multiple
            use-chips
            hide-bottom-space
            dense
            outlined
            :name="undefined"
            v-bind="filteredAttrs"
            :options="filteredOptions"
            @filter="filterFn"
            :disable="isDisabled"
            emit-value
            map-options
            :error="error"
            :error-message="errorMessage"
        >
            <template v-for="(_, slot) in $slots" v-slot:[slot]="scope">
                <slot :name="slot" v-bind="scope || {}"></slot>
            </template>
        </q-select>
        <template v-if="name">
            <input
                type="hidden"
                v-for="val in internalValue"
                :key="val"
                :name="inputName"
                :value="val"
                :disabled="isDisabled"
            />
        </template>
    </div>
</template>

<script>
import { ref, watch, computed, onMounted, onBeforeUnmount } from "vue";

export default {
    name: "MultiSelectWrapper",
    inheritAttrs: false,
    props: {
        modelValue: {
            type: Array,
            default: () => [],
        },
        name: String,
        error: Boolean,
        errorMessage: String,
        options: {
            type: Array,
            default: () => [],
        },
    },
    setup(props, { attrs }) {
        const root = ref(null);
        const internalValue = ref(
            Array.isArray(props.modelValue) ? props.modelValue : [],
        );

        // Determine initial disabled state from attrs
        const initialDisabled =
            attrs.disabled !== undefined && attrs.disabled !== false;
        const isDisabled = ref(initialDisabled);

        watch(
            () => props.modelValue,
            (val) => {
                internalValue.value = Array.isArray(val) ? val : [];
            },
        );

        const inputName = computed(() => {
            if (!props.name) return "";
            return props.name.endsWith("[]") ? props.name : props.name + "[]";
        });

        const filteredOptions = ref(props.options);

        watch(
            () => props.options,
            (val) => {
                filteredOptions.value = val || [];
            },
        );

        const filterFn = (val, update) => {
            if (val === "") {
                update(() => {
                    filteredOptions.value = props.options;
                });
                return;
            }

            update(() => {
                const needle = val.toLowerCase();
                filteredOptions.value = props.options.filter(
                    (v) => v.label.toLowerCase().indexOf(needle) > -1,
                );
            });
        };

        // Filter attrs to avoid passing 'disabled', 'class', 'style' to q-select via v-bind
        const filteredAttrs = computed(() => {
            const { disabled, class: cls, style, options, ...rest } = attrs;
            return rest;
        });

        let observer = null;

        const checkDisabled = () => {
            if (!root.value) return;
            // Check if disabled attribute is present on the root element
            const hasDisabled =
                root.value.hasAttribute("disabled") ||
                root.value.getAttribute("data-disabled") === "true";
            isDisabled.value = hasDisabled;
        };

        onMounted(() => {
            if (root.value) {
                // Apply initial disabled attribute to root element if needed, so that MutationObserver and CSS work
                if (initialDisabled && !root.value.hasAttribute("disabled")) {
                    root.value.setAttribute("disabled", "disabled");
                }
                // Double check disabled state
                checkDisabled();

                observer = new MutationObserver((mutations) => {
                    mutations.forEach((mutation) => {
                        if (
                            mutation.type === "attributes" &&
                            (mutation.attributeName === "disabled" ||
                                mutation.attributeName === "data-disabled")
                        ) {
                            checkDisabled();
                        }
                    });
                });
                observer.observe(root.value, { attributes: true });
            }
        });

        onBeforeUnmount(() => {
            if (observer) observer.disconnect();
        });

        return {
            internalValue,
            inputName,
            root,
            isDisabled,
            filteredAttrs,
            filteredOptions,
            filterFn,
        };
    },
};
</script>
